import axios, { AxiosResponse } from "axios";

import { getAccessToken } from "../helper/storage";
import config from "../config/config";
import { withData, withError } from "./api";
import { eventEmitter } from "../eventEmitters/EventEmitters";
import { isShareView } from "../share/shareView";

export const http = axios.create({
  baseURL: config.baseUrl,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});
// DEMO BUILD ONLY (REACT_APP_DEMO=1, see src/demo/): every request is answered from memory by an in-browser fake server, so the public demo costs the app server nothing.
// The flag is replaced at build time, so this whole block is removed from normal builds.
if (process.env.REACT_APP_DEMO === "1") {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require("../demo/fakeServer").installFakeServer(http);
}
// A RECIPIENT'S VIEWER (a shared replay clip's page, /s/<token>, in a build with sharing on): the dashboard is answered in the page from the clip, and no request it makes ever leaves the browser (src/share/viewerServer.ts).
// A recipient may be a Skydive customer in this very browser: the clip's page must never be able to speak to the real API with their cookies.
if (process.env.REACT_APP_DEMO !== "1" && isShareView()) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require("../share/viewerServer").installViewerServer(http);
}
http.interceptors.request.use((req) => {
  if (getAccessToken && req.headers)
    req.headers.authorization = `Bearer ${getAccessToken()}`;
  return req;
});

http.interceptors.response.use(
  (res) => withData(res.data) as AxiosResponse<any>,
  (err) => {
    /*
     * 🔴 `err.response?.data.message` - `response` was optional-chained but
     * `data` was NOT (fixed 2026-09-10).
     *
     * An error response with no body makes that `undefined.message`, which
     * throws INSIDE the interceptor. An interceptor that throws rejects the
     * promise, so every caller awaiting it throws too - and callers here are
     * written as `await ...` with no try/catch, so their `setLoading(false)`
     * never runs and the spinner hangs forever.
     *
     * Reported as: "I hit the button, it showed the loading spinner once, and
     * never did anything after that." The trigger was a 502 while the API was
     * restarting mid-deploy - a transient blip that should have shown an
     * error, not bricked the page until a reload.
     */
    if (err.response?.data?.message === "unauthorized") {
      eventEmitter.emit("logout");
 

}
    return withError(err?.response?.data);
  }
);

export function get<P>(url: string, params?: P): Promise<any> {
  return http({
    method: "get",
    url,
    params,
  });
}

export function post<D, P>(url: string, data: D, params?: P) :Promise<any> {

  return http({
    method: "post",
    url,
    data,
    params,
  });


}

export function postFile<D, P>(url: string, data: D, params?: P) {
  return http({
    method: "post",
    headers: { "Content-Type": "multipart/form-data" },
    url,
    data,
    params,
  });
}

export function put<D, P>(url: string, data: D, params?: P): Promise<any> {
  return http({
    method: "put",
    url,
    data,
    params,
  });
}

export function patch<D, P>(url: string, data: D, params?: P) {
  return http({
    method: "patch",
    url,
    data,
    params,
  });
}

export function remove<P>(url: string, params?: P) {
  return http({
    method: "delete",
    url,
    params,
  });
}
